<script setup lang="ts">
import { computed } from "vue";
import { type RouteLocationRaw, RouterLink, useRoute, useRouter } from "vue-router";

import { useInflectionTrainerWordClass } from "@/common/composables/use-inflection-trainer-word-class";
import { MatomoHelper } from "@/helpers";
import { changeQueryParam } from "@/router/util/change-query-param";
import { MatomoEventAction, MatomoEventCategory } from "@/types";
import type { FuzzySuggestions, WordClass } from "@/types/inflection-trainer.type";

const props = defineProps<{
  suggestions: FuzzySuggestions[];
  wordClass: WordClass;
}>();

const emit = defineEmits<{
  search: [{ suggestion: string; wordClass: WordClass }];
  changeWordClass: [wordClass: WordClass];
}>();

const route = useRoute();
const router = useRouter();

interface TranslatedFuzzySuggestions extends FuzzySuggestions {
  translatedWordClass: string;
}

const hasSuggestionToWordClass = computed<boolean>(() =>
  props.suggestions.some((suggestion) => suggestion.wordClass === props.wordClass)
);

const suggestions = computed<TranslatedFuzzySuggestions[]>(() => {
  const filteredSuggestions = props.suggestions.filter((suggestion) => {
    if (hasSuggestionToWordClass.value) {
      return suggestion.wordClass === props.wordClass;
    }

    return suggestion.wordClass !== props.wordClass;
  });

  return filteredSuggestions.map((suggestion) => ({
    ...suggestion,
    translatedWordClass: useInflectionTrainerWordClass(suggestion.wordClass).translatedWordClass
      .value
  }));
});

const buildRoute = (suggestion: string, wordClass: WordClass): RouteLocationRaw => ({
  ...route,
  query: {
    ...route.query,
    query: suggestion,
    wordClass
  }
});

const onSuggestionClick = (event: PointerEvent, suggestion: string, wordClass: WordClass) => {
  const label = hasSuggestionToWordClass.value
    ? "Clicked the fuzzy suggestion"
    : "Clicked the fuzzy suggestion from another wordclass";

  MatomoHelper.sendEvent(
    MatomoEventCategory.AbcWorkshop,
    MatomoEventAction.ClickInflectionTool,
    label
  );

  if (event.ctrlKey || event.metaKey || event.shiftKey) {
    // Let default event handling take over
    return;
  }

  emit("search", { suggestion, wordClass });
};
</script>

<template>
  <div class="fuzzy-suggestions-message">
    {{ $t("inflection-trainer.fuzzy-suggestions-message.intro") }}
    <span v-for="(group, groupIndex) in suggestions" :key="groupIndex">
      <span v-for="(suggestion, suggestionIndex) in group.suggestions" :key="suggestionIndex">
        <i18n-t keypath="inflection-trainer.fuzzy-suggestions-message.suggestion" tag="span">
          <template #suggestion>
            <RouterLink
              :to="buildRoute(suggestion, group.wordClass)"
              class="fuzzy-suggestions-message__link"
              @click="onSuggestionClick($event, suggestion, group.wordClass)"
              >{{ suggestion }}</RouterLink
            >
          </template>
        </i18n-t>

        <i18n-t
          v-if="suggestionIndex === group.suggestions.length - 1 && !hasSuggestionToWordClass"
          keypath="inflection-trainer.fuzzy-suggestions-message.word-class"
          tag="span"
          class="fuzzy-suggestions-message__word-class"
        >
          <template #wordClass>
            <RouterLink
              :to="changeQueryParam(router, 'wordClass', group.wordClass)"
              class="fuzzy-suggestions-message__link"
              @click="emit('changeWordClass', group.wordClass)"
              >{{ group.translatedWordClass }}</RouterLink
            >
          </template>
        </i18n-t>

        <span v-if="suggestionIndex < group.suggestions.length - 2">{{
          $t("inflection-trainer.fuzzy-suggestions-message.comma")
        }}</span>
        <span v-else-if="suggestionIndex === group.suggestions.length - 2">{{
          $t("inflection-trainer.fuzzy-suggestions-message.or")
        }}</span>
      </span>

      <span v-if="groupIndex < suggestions.length - 2">{{
        $t("inflection-trainer.fuzzy-suggestions-message.comma")
      }}</span>
      <span v-else-if="groupIndex === suggestions.length - 2">{{
        $t("inflection-trainer.fuzzy-suggestions-message.or")
      }}</span>
    </span>
    <span>{{ $t("inflection-trainer.fuzzy-suggestions-message.ending") }}</span>
  </div>
</template>

<style lang="less" scoped>
@import "@/less/components/inflection-trainer/fuzzy-suggestions-message.less";
</style>
